import * as React from 'react'
import { RouteComponentProps } from 'react-router'
import PinLocationModal from 'src/components/PinLocationModal'
import LocationItem from 'src/components/riders/common/LocationItem'
import { withNotification } from 'src/components/ui/notification-provider'
import ServiceEditBottomBar from 'src/containers/services/ServiceEditBottomBar'
import { SERVICE_EDIT_URL } from 'src/state/constants'
import { IGoogleMapBoundsDTO, IStateMetadata, IAutoComplete, IRiderLocationObject } from 'src/types'
import { IServiceLocationObject } from 'src/types/service'
import { searchLocation, getPlaceDetails } from 'src/utils/location'
import UIMessages from 'src/utils/ui-messages'

interface IServiceEditSuggestedLocationsProps {
  serviceId: string
  suggestedLocationList: IServiceLocationObject[]
  zoom: number
  bounds: IGoogleMapBoundsDTO
  getSuggestedLocationList: (serviceId: string) => Promise<IServiceLocationObject[]>
  saveLocation: (location: IServiceLocationObject) => Promise<any>
  createLocation: (serviceId: string, location: IServiceLocationObject) => Promise<IServiceLocationObject>
  deleteLocation: (id: string) => Promise<any>
  showMessage: (error: string, isError: boolean) => void
  autoComplete: IAutoComplete[]
  showAutoComplete: (e: any) => Promise<any>
  clearAutoComplete: () => Promise<any>
  formRef: any
  onStepSuccessfullyCompleted?: () => void
  onCreateServiceCompleted?: () => void
  handleSubmit: () => void
  onRef: any
  notification?: any
  routeProps: RouteComponentProps<any>
}

interface ISuggestedLocationsState {
  isSaved: boolean
  isSaving: boolean
  isOpen: boolean
  index: number
  locations: IServiceLocationObject[]
  locationIdsToUpdate: string[]
  newLocation: IServiceLocationObject | null
  locationToPin: IServiceLocationObject | null
  autoCompleteLocations: IServiceLocationObject[]
}

class ServiceEditSuggestedLocations extends React.Component<
  IServiceEditSuggestedLocationsProps,
  ISuggestedLocationsState
> {
  constructor(props: IServiceEditSuggestedLocationsProps) {
    super(props)
    this.state = {
      isSaved: true,
      isSaving: false,
      locations: [],
      locationIdsToUpdate: [],
      newLocation: null,
      locationToPin: null,
      isOpen: false,
      index: -1,
      autoCompleteLocations: []
    }
    this.props.showMessage('', false)
  }

  public componentDidMount() {
    this.props.onRef(this)
    console.log(this.props.onRef)
    this.props.getSuggestedLocationList(this.props.serviceId)
    if (this.props.suggestedLocationList && this.props.suggestedLocationList.length > 0) {
      this.setPropsToState()
    }
  }

  public render() {
    const { isSaving, locations, newLocation, index, locationIdsToUpdate } = this.state
    const listOfLocationsStyle: any = {
      pointerEvents: isSaving ? 'none' : 'auto',
      opacity: isSaving ? 0.4 : 1.0
    }
    const addNewLocationButtonStyle: any = {
      pointerEvents: !!newLocation ? 'none' : 'auto',
      opacity: !!newLocation ? 0.4 : 1.0
    }

    return (
      <section>
        {this.state.isOpen && (
          <PinLocationModal
            zoom={this.props.zoom}
            bounds={this.props.bounds}
            location={this.state.locationToPin!}
            locations={this.state.locations}
            onCancel={this.onCancel}
            saveLocation={this.onModalLocationSaved}
          />
        )}
        <div style={listOfLocationsStyle} className='bg-white'>
          {locations.map((it, i) => (
            <LocationItem
              index={i}
              key={`tmli-${i}`}
              allowToEdit={!newLocation}
              location={it}
              searchLocation={this.searchLocation}
              onLocationSelect={this.onLocationSelect}
              autoCompleteLocations={index === i ? this.state.autoCompleteLocations : []}
              onCancel={this.onCancel}
              saveLocation={this.onSaveLocation}
              showMessage={this.props.showMessage}
              updateLocation={this.onUpdateLocation}
              openModal={this.openModal}
              deleteLocation={this.deleteLocation}
            />
          ))}
          {!!newLocation && (
            <LocationItem
              index={-1}
              allowToEdit={true}
              location={newLocation}
              searchLocation={this.searchLocation}
              onLocationSelect={this.onLocationSelect}
              autoCompleteLocations={index === -1 ? this.state.autoCompleteLocations : []}
              onCancel={this.onCancel}
              saveLocation={this.onSaveLocation}
              showMessage={this.props.showMessage}
              updateLocation={this.onUpdateLocation}
              openModal={this.openModal}
            />
          )}
        </div>
        <div style={addNewLocationButtonStyle} className='ml2 pl2 bold font-small mt1 mb4'>
          <a href='#' onClick={this.onCreateLocation}>
            + {UIMessages.common.addLocation}
          </a>
        </div>
      </section>
    )
  }

  public onSave = () => {
    const { locations, locationIdsToUpdate } = this.state
    this.setState({ isSaving: true })

    const locationsToUpdate = locations.filter((l: IServiceLocationObject) => locationIdsToUpdate.indexOf(l.id!) > -1)
    for (const location of locationsToUpdate) {
      if (location.fullAddress.length < 3) {
        this.showErrorMessage(UIMessages.rider.validation.locationAddressMinLength)
        return
      }

      this.props.saveLocation(location).then(
        () => {
          this.setState({ isSaving: true })
        },
        () => this.setState({ isSaved: false, isSaving: false })
      )
    }
    this.setState({ isSaving: false, isSaved: true, locationIdsToUpdate: [] })

    if (Object.keys(locations).length > 0) {
      this.props.notification.addNotification({
        title: 'Successfully saved locations',
        level: 'success'
      })
      this.handleDataSaved(`/${SERVICE_EDIT_URL.SUGGESTED_LOCATIONS}`)()
    } else {
      this.props.notification.addNotification({
        title: 'Please create at least one location!',
        level: 'error'
      })
    }
  }

  private handleDataSaved = (url: string) => () => {
    this.props.routeProps.history.push(`/services/${this.props.serviceId}${url}`)
  }

  private onModalLocationSaved = (location: IServiceLocationObject) => {
    this.onUpdateLocation(location, this.state.index)
  }

  private searchLocation = async (address: string, index: number) => {
    if (!address) {
      this.setState({
        autoCompleteLocations: []
      })
      return
    }
    const autoCompleteLocations = await searchLocation({ input: address })
    this.setState({
      autoCompleteLocations,
      index
    })
  }

  private formatAddress = (data: any): string => {
    const name = data.placeName ? `${data.placeName}: ` : ''
    const street = data.street ? `${data.street}, ` : ''
    const state = data.state ? `${data.state} ` : ''
    const zipCode = data.zip ? `${data.zip}, ` : ''
    const country = data.country ? data.country : ''
    return `$public onSave = () => {
      const { locations, locationIdsToUpdate } = this.state
      this.setState({ isSaving: true })
  
      const locationsToUpdate = locations.filter((l: IServiceLocationObject) => locationIdsToUpdate.indexOf(l.id!) > -1)
      for (const location of locationsToUpdate) {
        if (location.fullAddress.length < 3) {
          this.showErrorMessage(UIMessages.rider.validation.locationAddressMinLength)
          return
        }
  
        this.props.saveLocation(location).then(
          () => {
            this.setState({ isSaving: true })
          },
          () => this.setState({ isSaved: false, isSaving: false })
        )
  
      }
      this.setState({ isSaving: false, isSaved: true, locationIdsToUpdate: [] })
  
  
    }{name}${street}${state}${zipCode}${country}`
  }
  private onLocationSelect = async (selectedLocation: any, index: number) => {
    const { locations, locationIdsToUpdate } = this.state
    const { googlePlaceId } = selectedLocation

    const data = await getPlaceDetails(googlePlaceId)
    const location = { ...selectedLocation, ...data }
    // location.fullAddress = data ? this.formatAddress(data) : selectedLocation.fullAddress
    /* New Updates */
    location.fullAddress = selectedLocation.fullAddress ? selectedLocation.fullAddress : this.formatAddress(data) //display full address
    //if user typed a name before address earch, keep it
    location.placeName = selectedLocation.placeName || location.placeName

    if (selectedLocation.isNew) {
      this.setState({
        newLocation: location,
        autoCompleteLocations: []
      })
    } else {
      const foundIndex = locationIdsToUpdate.indexOf(location.id)
      locations[index] = location
      this.setState({
        locations,
        autoCompleteLocations: [],
        locationIdsToUpdate: foundIndex < 0 ? [...locationIdsToUpdate, location.id] : locationIdsToUpdate
      })
    }
  }

  private onUpdateLocation = (location: IServiceLocationObject, index: number) => {
    const { locations, locationIdsToUpdate } = this.state

    if (location.isNew) {
      this.setState({
        isSaved: false,
        newLocation: location,
        isOpen: false
      })
    } else {
      const id = location.id!
      const foundIndex = locationIdsToUpdate.indexOf(id)
      locations[index] = location
      this.setState({
        locations,
        locationIdsToUpdate: foundIndex < 0 ? [...locationIdsToUpdate, id] : locationIdsToUpdate,
        isSaved: false,
        isOpen: false
      })
    }
  }

  private onSaveLocation = (location: IServiceLocationObject) => {
    const { locations } = this.state

    this.props.createLocation(this.props.serviceId, location).then(
      (createdLoc: IServiceLocationObject) => {
        this.setState({
          isSaved: false,
          locations: [...this.state.locations, createdLoc],
          newLocation: null
        })
      },
      () => {
        this.setState({
          isSaved: false
        })
      }
    )
  }

  private deleteLocation = (index: number) => {
    const { locations, locationIdsToUpdate } = this.state
    const location = locations[index]
    const id = location.id!
    const newLocations = [...locations.slice(0, index), ...locations.slice(index + 1)]
    const foundIndex = locationIdsToUpdate.indexOf(id)

    this.props.deleteLocation(id).then(
      () =>
        this.setState({
          locations: newLocations,
          locationIdsToUpdate: [
            ...locationIdsToUpdate.slice(0, foundIndex),
            ...locationIdsToUpdate.slice(foundIndex + 1)
          ]
        }),
      () =>
        this.setState({
          locations
        })
    )
  }

  private onCancel = () => {
    this.setState({
      isSaved: false,
      isOpen: false,
      newLocation: null
    })
  }

  private onCreateLocation = () => {
    this.setState({
      isSaved: false,
      newLocation: { isNew: true } as any
    })
  }

  private setPropsToState = () => {
    this.setState({
      locations: this.props.suggestedLocationList
    })
  }

  private closeModal = () => {
    this.setState({
      isOpen: false
    })
  }

  private openModal = (location: IServiceLocationObject, index: number) => {
    if (!location.coordinates || (location.coordinates.lat === 0 && location.coordinates.lng === 0)) {
      return
    }

    this.setState({
      isOpen: true,
      index,
      locationToPin: location
    })
    this.props.showMessage('', false)
  }

  private showErrorMessage = (message: string) => {
    this.props.showMessage(message, true)
    this.setState({ isSaving: false })
  }
}

export default withNotification(ServiceEditSuggestedLocations)



// WEBPACK FOOTER //
// ./src/components/services/edit/ServiceEditSuggestedLocations.tsx